import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSignature, FileSpreadsheet, FileType, ShieldCheck, AlertCircle, Loader2 } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import api, { downloadExport, formatApiErrorDetail } from "@/lib/api";

export default function AdminContracts() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/admin/contract-acceptances").then((r) => setData(r.data)).catch(() => { setError(true); toast.error("Errore nel caricamento."); });
  }, []);

  const run = async (path, name) => {
    try {
      await downloadExport(path, name);
      toast.success("Esportazione avviata.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (!data) {
    return (
      <DashboardLayout title="Registro firme" admin>
        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700" data-testid="admin-contracts-error">
            Impossibile caricare il registro firme. Riprova più tardi.
          </div>
        ) : (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento…</div>
        )}
      </DashboardLayout>
    );
  }

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT") : "—");

  return (
    <DashboardLayout title="Registro firme" admin>
      <div className="space-y-6" data-testid="admin-contracts-page">
        {/* summary + export */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0A1128] text-[#D4AF37]">
              <FileSignature className="h-6 w-6" />
            </span>
            <div>
              <p className="font-display text-lg font-semibold text-slate-900">
                {data.signed_current} / {data.total_segnalatori} segnalatori hanno firmato la versione {data.current_version}
              </p>
              <p className="text-sm text-slate-500">Contratto vigente: versione {data.effective_date} ({data.current_version})</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => run("/admin/export/contract-acceptances.csv", "registro-firme-contratto.csv")}
              data-testid="export-contracts-csv"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <FileSpreadsheet className="h-4 w-4" /> CSV
            </button>
            <button onClick={() => run("/admin/export/contract-acceptances.pdf", "registro-firme-contratto.pdf")}
              data-testid="export-contracts-pdf"
              className="btn-gold inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold">
              <FileType className="h-4 w-4" /> PDF
            </button>
          </div>
        </div>

        {/* acceptances table */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            <h3 className="font-display text-base font-semibold text-slate-900">Firme registrate</h3>
          </div>
          {data.acceptances.length === 0 ? (
            <p className="px-6 py-10 text-center text-slate-400">Nessuna firma registrata.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="acceptances-table">
                <thead>
                  <tr className="text-left text-xs uppercase text-slate-400 border-b border-slate-100">
                    <th className="px-6 py-3 font-semibold">Segnalatore</th>
                    <th className="px-6 py-3 font-semibold">Email</th>
                    <th className="px-6 py-3 font-semibold">Versione</th>
                    <th className="px-6 py-3 font-semibold">Firmato il</th>
                  </tr>
                </thead>
                <tbody>
                  {data.acceptances.map((a) => (
                    <tr key={a.id} className="border-b border-slate-50 last:border-0" data-testid="acceptance-row">
                      <td className="px-6 py-3 font-medium text-slate-800">{a.user_name || "—"}</td>
                      <td className="px-6 py-3 text-slate-500">{a.user_email || "—"}</td>
                      <td className="px-6 py-3">
                        <span className="inline-flex rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-0.5 text-xs font-medium">{a.version}</span>
                      </td>
                      <td className="px-6 py-3 text-slate-600">{fmt(a.accepted_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* pending */}
        {data.pending.length > 0 && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 overflow-hidden" data-testid="pending-block">
            <div className="px-6 py-4 border-b border-amber-100 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              <h3 className="font-display text-base font-semibold text-amber-800">In attesa di firma ({data.pending.length})</h3>
            </div>
            <div className="divide-y divide-amber-100">
              {data.pending.map((p) => (
                <div key={p.user_id} className="px-6 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{p.name || "—"}</p>
                    <p className="text-xs text-slate-500">{p.email}</p>
                  </div>
                  <span className="text-xs text-amber-700">
                    {p.accepted_version ? `Versione obsoleta (${p.accepted_version})` : "Non firmato"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
